import { ReactComponent as IconDragHandle } from "assets/svg/icon-drag-handle.svg";
import CopyToClipboardButton from "components/copy-to-clipboard-button/CopyToClipboardButton";
import RequiredAsterisk from "components/required-asterisk";
import { usePhonePortraitContext } from "contexts/phone-portrait-context";
import messages from "messages";
import { ChangeEvent, forwardRef, useEffect, useMemo } from "react";
import { Form } from "react-bootstrap";
import { useLocation } from "react-router-dom";
import copyHtmlAsRichText from "utils/clipboard/copyHtmlAsRichText";
import noteElementToHtml from "utils/clipboard/noteElementToHtml";
import getClasses from "utils/getClasses";
import BlockAttachments from "../../../block-attachments/BlockAttachments";
import type {
  CheckboxesJSONSchema,
  DndItemProp,
  FreeTextJSONSchema,
  RadiosJSONSchema,
  RatingsJSONSchema,
} from "../../types";
import { getIsRatingsBlock } from "../../utils/getIsRatingsBlock";
import { getIsSectionBlock } from "../../utils/getIsSectionBlock";
import styles from "./BlockType.module.scss";
import Actions from "./components/actions";
import Checkboxes from "./components/checkboxes";
import CustomReadonlyInput from "./components/custom-readonly-input";
import FreeText from "./components/free-text";
import Paragraph from "./components/paragraph";
import Radios from "./components/radios";
import Ratings from "./components/ratings";
import ShortAnswer from "./components/short-answer";
import { useBlockType } from "./contexts/BlockTypeContext";
import { getIsFreeTextBlock } from "./utils/getIsFreeTextBlock";

type Props = {
  id?: string;
  uiSchema: UISchema;
  jsonSchema: JSONSchema;
  parent?: string;
  expanded: boolean;

  onAddSibling?: (arg: DndItemProp, dropId: string, after: boolean) => void;
  onRemoveFromParent?: (arg: string) => void;
  required?: boolean;
  onRequiredChange?: (arg: boolean) => void;
  style?: any;
  isDragging?: boolean;
  isDraggingLayer?: boolean;
  suicidal?: boolean;
  gptBusy?: boolean;
};

const BlockType = forwardRef<HTMLDivElement, Props>(function BlockTypeNoRef(
  {
    id,
    uiSchema,
    jsonSchema,
    onAddSibling,
    onRemoveFromParent,
    isDragging,
    parent,
    expanded,
    required,
    onRequiredChange,
    style,
    isDraggingLayer,
    suicidal,
    gptBusy,
  }: Props,
  passedRef,
) {
  const { isPhonePortrait } = usePhonePortraitContext();
  const { pathname } = useLocation();
  const showReviewPageStyle = useMemo(() => {
    return pathname.startsWith("/pro-dashboard/session-review/");
  }, [pathname]);

  const {
    mode,
    isEdit,
    isFill,
    value: formData,
    onChange,
    onUpload,
  } = useBlockType();

  // pass id to the event handler
  const handleUpload = useMemo(() => {
    if (!onUpload || !id) {
      return undefined;
    }

    return (e: ChangeEvent<HTMLInputElement>) =>
      onUpload && id && onUpload(e, id);
  }, [id, onUpload]);

  // sanity check
  useEffect(() => {
    let nextUISchema: UISchema | undefined = undefined;
    let nextJSONSchema: JSONSchema | undefined = undefined;

    if (!jsonSchema) {
      nextJSONSchema = {};
    }

    if (!uiSchema) {
      nextUISchema = {};
    }

    if (nextUISchema || nextJSONSchema) {
      return (
        onChange &&
        onChange({
          uiSchema: nextUISchema || uiSchema,
          jsonSchema: nextJSONSchema || jsonSchema,
        })
      );
    }
  }, [jsonSchema, uiSchema, onChange]);

  const isSection = getIsSectionBlock(jsonSchema, uiSchema);
  const isFreeText = getIsFreeTextBlock(jsonSchema, uiSchema);
  const isRatings = getIsRatingsBlock(uiSchema);

  const handleChangeJsonSchema = (jsonSchema) =>
    onChange &&
    onChange({
      uiSchema,
      jsonSchema,
    });

  const copyBlockToClipboard = () =>
    copyHtmlAsRichText(noteElementToHtml(jsonSchema, formData));

  return (
    <div
      style={style}
      ref={passedRef}
      className={getClasses(
        styles["block-type"],
        styles[`block-type--${mode}`],
        parent && styles["block-type--is-child"],
        isSection && styles["block-type--is-section"],
        !expanded && styles["block-type--collapsed"],
        isDragging && styles["block-type--dragging"],
        isDragging && !isDraggingLayer && styles["block-type--transparent"],
        isDraggingLayer && styles["block-type--dragging-layer"],
      )}
    >
      <div
        className={
          !showReviewPageStyle
            ? getClasses(
                styles["block-type__body"],
                styles[`block-type__body--${mode}`],
                isPhonePortrait && styles["block-type__body--phone"],
              )
            : getClasses(
                styles["block-type__body--review-page"],
                isPhonePortrait && styles["block-type__body--phone"],
              )
        }
      >
        {isEdit && (
          <div className={styles["block-type__drag-handle"]}>
            <IconDragHandle />
          </div>
        )}
        <div
          className={getClasses(
            !showReviewPageStyle
              ? `${styles["block-type__inputs"]} ${
                  styles[`block-type__inputs--${mode}`]
                }`
              : styles["block-type__inputs--review-page"],
            isPhonePortrait && styles["block-type__inputs--phone"],
          )}
        >
          {!isFreeText && (
            <Form.Group className={styles["block-type__title-group"]}>
              <CustomReadonlyInput
                value={jsonSchema.title || ""}
                onChange={(e) =>
                  onChange &&
                  onChange({
                    jsonSchema: {
                      ...jsonSchema,
                      title: e.target.value,
                    },
                    uiSchema,
                  })
                }
                placeholder={
                  isSection
                    ? messages.untitledSectionTitle
                    : messages.untitledHeader
                }
                className={getClasses(
                  styles["block-type__title"],
                  styles[`block-type__title--${mode}`],
                  isSection && styles["block-type__title--section"],
                  isSection && styles[`block-type__title--section--${mode}`],
                  isFill && styles["block-type__title--is-fill"],
                )}
              >
                {/* required symbol */}
                {isFill && required && (
                  <RequiredAsterisk
                    className={styles["block-type__title-required"]}
                  />
                )}
              </CustomReadonlyInput>
              {isFill && (
                <CopyToClipboardButton
                  onClick={copyBlockToClipboard}
                  size="small"
                />
              )}
            </Form.Group>
          )}
          {!isSection || (!isEdit && !jsonSchema.description) ? null : (
            <CustomReadonlyInput
              className={[
                styles["block-type__string-input"],
                styles["block-type__string-input--no-border"],
              ].join(" ")}
              placeholder={"Description (optional)"}
              readOnly={!isEdit}
              value={jsonSchema.description || ""}
              onChange={(e) => {
                jsonSchema.description = e.target.value || "";
                return (
                  onChange &&
                  onChange({
                    jsonSchema,
                    uiSchema,
                  })
                );
              }}
            />
          )}
          {(!isSection || (isSection && expanded)) && (
            <>
              {!uiSchema["ui:widget"] && jsonSchema.type === "string" && (
                <ShortAnswer
                  value={formData || ""}
                  onChange={onChange}
                  onUpload={handleUpload}
                  gptBusy={gptBusy}
                />
              )}
              {!isFreeText &&
                uiSchema["ui:widget"] === "textarea" &&
                jsonSchema.type === "string" && (
                  <Paragraph
                    value={formData || ""}
                    onChange={onChange}
                    onUpload={handleUpload}
                    gptBusy={gptBusy}
                  />
                )}
              {uiSchema["ui:widget"] === "checkboxes" && (
                <Checkboxes
                  jsonSchema={jsonSchema as CheckboxesJSONSchema}
                  onChangeJsonSchema={handleChangeJsonSchema}
                  gptBusy={gptBusy}
                />
              )}
              {uiSchema["ui:widget"] === "radio" && (
                <Radios
                  jsonSchema={jsonSchema as RadiosJSONSchema}
                  onChangeJsonSchema={handleChangeJsonSchema}
                  gptBusy={gptBusy}
                />
              )}
              {isFreeText && (
                <FreeText
                  jsonSchema={jsonSchema as FreeTextJSONSchema}
                  onChangeJsonSchema={handleChangeJsonSchema}
                />
              )}
              {isRatings && (
                <Ratings
                  jsonSchema={jsonSchema as RatingsJSONSchema}
                  onChangeJsonSchema={handleChangeJsonSchema}
                  suicidal={suicidal}
                  gptBusy={gptBusy}
                />
              )}
              {id && isEdit && (
                <Actions
                  id={id}
                  className={styles["block-type__actions"]}
                  jsonSchema={jsonSchema}
                  uiSchema={uiSchema}
                  required={required}
                  onAddSibling={onAddSibling}
                  onRemoveFromParent={onRemoveFromParent}
                  onRequiredChange={onRequiredChange}
                  isSection={isSection}
                />
              )}
            </>
          )}
          {id && <BlockAttachments id={id} mode={mode} />}
        </div>

        {isEdit && <div className={styles["block-type__active-border"]} />}
      </div>
    </div>
  );
});

export default BlockType;
